import { Injectable } from '@angular/core';
import { Device } from '@capacitor/device';
import { Settings } from '../models/settings';
import { StorageService } from './storage.service';
import { environment } from '../../environments/environment';
import { EventEmitter } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  private readonly SETTINGS_KEY: string;
  public settingsChanged = new EventEmitter<Settings>();

  constructor(private storageService: StorageService) {
    // Use the app flavor from environment to create a unique settings key
    this.SETTINGS_KEY = `settings_${environment.appFlavor}`;
  }

  async getSettings(): Promise<Settings | null> {
    return await this.storageService.get(this.SETTINGS_KEY);
  }

  async saveSettings(settings: Settings): Promise<void> {
    await this.storageService.set(this.SETTINGS_KEY, settings);
    this.settingsChanged.emit(settings);
  }

  async updateSettings(partialSettings: Partial<Settings>): Promise<void> {
    const currentSettings = await this.getSettings() || {};
    const updatedSettings = { ...currentSettings, ...partialSettings };
    await this.saveSettings(updatedSettings as Settings);
  }

  async resetSettings(): Promise<void> {
    // Remove settings from storage
    await this.storageService.delete(this.SETTINGS_KEY);
    
    // Create default settings with device ID
    const deviceId = await this.getDeviceId();
    const defaultSettings: Settings = {
      isAnonymous: false,
      observerId: null,
      firstName: null,
      lastName: null,
      email: null,
      phone: null,
      deviceId: deviceId || '',
      language: null,
      defaultQuestion: null,
      role: 'submitter'
    };
    
    // Save default settings
    await this.saveSettings(defaultSettings);
  }

  async getDeviceId(): Promise<string> {
    const deviceInfo = await Device.getId();
    return deviceInfo.identifier;
  }
}
